// Researcher-facing data dictionary for EdgeCare.
// Field names, types, enum values and ranges are ground-truthed against the
// live `edgecare_sensor_events` table; cadence/permission/privacy notes are
// verified against the mobile collector code. Keep this in sync when the
// mobile app changes what it collects.

export interface Field {
  name: string;
  type: string;
  values?: string;
  desc: string;
}

export interface DataSource {
  source: string;
  title: string;
  measures: string;
  cadence?: string;
  method?: string;
  platform?: string;
  permission?: string;
  privacy?: string;
  notes?: string;
  tag?: "experimental" | "diagnostic" | "derived";
  fields: Field[];
}

export interface Category {
  id: string;
  title: string;
  blurb: string;
  sources: DataSource[];
}

export const CATEGORIES: Category[] = [
  {
    id: "activity",
    title: "Activity & Movement",
    blurb: "Physical activity state and step counts.",
    sources: [
      {
        source: "PHYSICAL_ACTIVITY",
        title: "Physical Activity",
        measures:
          "The participant's current physical-activity state and the transitions between states.",
        cadence:
          "Event-driven on activity changes, plus a periodic snapshot approximately every 15 minutes.",
        platform: "Android",
        permission: "Physical activity (ACTIVITY_RECOGNITION)",
        notes:
          'A few older records carry source="APP_START_SEED" and synthetic=true. These are synthetic seed rows written at app start, not real detections; exclude them.',
        fields: [
          {
            name: "activity",
            type: "string",
            values:
              "UNKNOWN · STATIONARY · WALKING · RUNNING · CYCLING · IN_VEHICLE",
            desc: "Detected activity at this transition (from Google Activity Recognition).",
          },
          {
            name: "confidence",
            type: "integer",
            values: "0–100",
            desc: "Detector confidence.",
          },
          {
            name: "source",
            type: "string | absent",
            values: "periodic · absent for transition records",
            desc: "Present on periodic snapshots; activity-transition records omit this field.",
          },
        ],
      },
      {
        source: "STEP_COUNT",
        title: "Step Count",
        measures: "Steps taken, read from the device's hardware step counter.",
        cadence:
          "Once per completed local day; the final total is recorded after the local date rolls over.",
        platform: "Android",
        permission: "Physical activity (ACTIVITY_RECOGNITION)",
        notes:
          "Uses the TYPE_STEP_COUNTER sensor with a per-day baseline so the count resets at local midnight; devices without that sensor produce no records.",
        fields: [
          {
            name: "steps",
            type: "integer",
            values: "≥ 0",
            desc: "Cumulative steps for the UTC day at the time of recording.",
          },
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "The device-local calendar day the final count covers.",
          },
          {
            name: "collected_at",
            type: "timestamp",
            desc: "When the count was recorded.",
          },
        ],
      },
    ],
  },
  {
    id: "phone",
    title: "Phone Use",
    blurb: "Screen activity, app usage, and notifications.",
    sources: [
      {
        source: "SCREEN_EVENT",
        title: "Screen State",
        measures: "Screen-interactive and keyguard lock-state transitions.",
        cadence:
          "Once per day, with one record for each screen or keyguard transition observed during the previous completed local day.",
        method:
          "Reads Android UsageEvents for the exact previous-day window (Android 9+).",
        platform: "Android",
        permission: "Usage access (PACKAGE_USAGE_STATS)",
        fields: [
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "The device-local calendar day containing the transition.",
          },
          {
            name: "state",
            type: "string",
            values: "ON · OFF · LOCKED · UNLOCKED",
            desc: "Whether the screen became interactive/non-interactive or the keyguard was shown/hidden.",
          },
          {
            name: "timestamp",
            type: "epoch ms",
            desc: "When the transition occurred.",
          },
        ],
      },
      {
        source: "APP_USAGE",
        title: "App Usage",
        measures:
          "Total interactive and visible time per app during a completed local day.",
        cadence:
          "Once per day; one record per app used the previous (completed) local day.",
        method:
          "Reconstructs an exact-day timeline from Android UsageEvents, clipping lifecycle, screen, and keyguard events to local-midnight boundaries.",
        platform: "Android",
        permission: "Usage access (PACKAGE_USAGE_STATS)",
        fields: [
          {
            name: "package_name",
            type: "string",
            desc: "Android package id (e.g. com.android.chrome).",
          },
          {
            name: "app_name",
            type: "string",
            desc: "Human-readable app label.",
          },
          {
            name: "app_category",
            type: "string",
            values:
              "social_media · streaming · gaming · media · news · navigation · productivity · accessibility · education · banking · shopping · health · dating · messaging · system · other",
            desc: "From Android's own app category, falling back to a package-name heuristic.",
          },
          {
            name: "interactive_seconds",
            type: "integer",
            values: "seconds",
            desc: "Time an app had a resumed activity while the screen was interactive and the keyguard was hidden.",
          },
          {
            name: "visible_seconds",
            type: "integer",
            values: "seconds",
            desc: "Total time the app had a visible activity during the day. On Android 10+, this can exceed interactive_seconds when an app remains visible without being active.",
          },
          {
            name: "last_used",
            type: "epoch ms",
            desc: "Last time the app was used (real timestamp, not a bucket boundary).",
          },
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "Local calendar day this record covers.",
          },
          {
            name: "timestamp_start",
            type: "epoch ms",
            desc: "Start of the day (local midnight) this record covers.",
          },
          {
            name: "timestamp_end",
            type: "epoch ms",
            desc: "Final millisecond of the local day this record covers.",
          },
        ],
      },
      {
        source: "NOTIFICATION_POSTED",
        title: "Notification Posted",
        measures:
          "Metadata for each notification as it is posted to the status bar.",
        cadence:
          "Event-driven, with one record each time a notification is posted.",
        method:
          "Captured by Android's NotificationListenerService when an app posts a notification",
        platform: "Android",
        permission: "Notification access (NotificationListenerService)",
        privacy:
          "Metadata only. Notification title, text, and people are never read. The notification key is stored only as a short hash (key_hash) so a post can be matched to its removal. The posting app's package is stored in the clear (same as App Usage). Foreground-service, ongoing, and group-summary notifications are skipped.",
        fields: [
          {
            name: "key_hash",
            type: "string",
            desc: "First 8 hex of SHA-256 of the notification key; joins this post to its NOTIFICATION_REMOVED record.",
          },
          {
            name: "package_name",
            type: "string",
            desc: "Posting app's package id (e.g. com.whatsapp).",
          },
          {
            name: "app_name",
            type: "string",
            desc: "Human-readable app label.",
          },
          {
            name: "category",
            type: "string",
            values: "e.g. msg · email · social · call · alarm · … · unknown",
            desc: 'The notification\'s declared category (Android Notification.category; "unknown" if unset).',
          },
          {
            name: "importance",
            type: "string",
            values:
              "none · min · low · default · high · max · unspecified · unknown",
            desc: "Channel importance for this notification.",
          },
          {
            name: "channel_id",
            type: "string",
            desc: "Notification channel id (empty on pre-Android 8 / when unknown).",
          },
          {
            name: "made_sound",
            type: "boolean",
            desc: "Whether the notification audibly alerted (Android 10+).",
          },
          {
            name: "is_ongoing",
            type: "boolean",
            desc: "Whether the notification is ongoing (non-dismissable).",
          },
          {
            name: "is_group_summary",
            type: "boolean",
            desc: "Whether this is a group-summary notification.",
          },
          {
            name: "post_time",
            type: "timestamp",
            desc: "When the notification was posted.",
          },
        ],
      },
      {
        source: "NOTIFICATION_REMOVED",
        title: "Notification Removed",
        measures:
          "Metadata for each notification as it is removed, with how long it was visible.",
        cadence:
          "Event-driven, with one record each time a notification is removed.",
        method:
          "Captured by Android's NotificationListenerService when a notification is removed, then matched to its NOTIFICATION_POSTED via key_hash to compute dwell time.",
        platform: "Android",
        permission: "Notification access (NotificationListenerService)",
        privacy:
          "Metadata only. No title, text, or people are read. Matches a NOTIFICATION_POSTED via key_hash.",
        fields: [
          {
            name: "key_hash",
            type: "string",
            desc: "First 8 hex of SHA-256 of the notification key; joins back to the NOTIFICATION_POSTED record.",
          },
          { name: "package_name", type: "string", desc: "App's package id." },
          {
            name: "app_name",
            type: "string",
            desc: "Human-readable app label.",
          },
          {
            name: "category",
            type: "string",
            desc: 'The notification\'s declared category ("unknown" if unset).',
          },
          {
            name: "reason",
            type: "string",
            values:
              "clicked · dismissed · dismissed_all · app_removed · listener_removed · group_summary_removed · timeout · snoozed · unknown",
            desc: "Why the notification was removed (from Android's removal reason).",
          },
          {
            name: "dwell_ms",
            type: "integer",
            values: "milliseconds",
            desc: "How long the notification was visible before removal (removed − posted), used as a time-to-interaction proxy.",
          },
          {
            name: "post_time",
            type: "timestamp",
            desc: "When the notification was originally posted.",
          },
        ],
      },
    ],
  },
  {
    id: "comms",
    title: "Communication",
    blurb:
      "Aggregate call and SMS activity: counts and durations only, never content.",
    sources: [
      {
        source: "CALL_LOG",
        title: "Call Log",
        measures: "One record per phone call observed in the window.",
        cadence:
          "Once per local day; re-queries the rolling previous 48 hours and deduplicates calls by Android row id and timestamp.",
        platform: "Android",
        permission: "Call log (READ_CALL_LOG)",
        privacy:
          "Phone numbers and contact names are never stored in the clear. Each is reduced to a short one-way hash (first 8 hex of SHA-256) so repeat contacts can be linked without revealing who. (An 8-char unsalted hash is a weak pseudonym, not true anonymization.) Call timing, type, and duration are stored; call audio/content is never accessed.",
        fields: [
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "UTC calendar day of the call.",
          },
          { name: "time", type: "timestamp", desc: "When the call occurred." },
          {
            name: "time_ms",
            type: "epoch ms",
            desc: "When the call occurred (epoch ms).",
          },
          {
            name: "type",
            type: "string",
            values:
              "INCOMING · OUTGOING · MISSED · REJECTED · BLOCKED · UNKNOWN",
            desc: "Call direction / disposition.",
          },
          {
            name: "duration",
            type: "integer",
            values: "seconds",
            desc: "Call duration (0 for missed / rejected / blocked).",
          },
          {
            name: "number",
            type: "string",
            desc: "Hashed phone number (8-hex SHA-256; empty if unavailable); links repeat callers, not the number itself.",
          },
          {
            name: "name",
            type: "string",
            desc: "Hashed cached contact name (8-hex SHA-256; empty if no contact).",
          },
          { name: "id", type: "integer", desc: "Android call-log row id." },
        ],
      },
      {
        source: "SMS_LOG",
        title: "SMS Log",
        measures: "One record per text message observed in the window.",
        cadence:
          "Every 15 minutes, with one record per message sent or received since the last check.",
        platform: "Android",
        permission: "SMS (READ_SMS)",
        privacy:
          "Message text and the other party's address are never stored in the clear. Each is reduced to a short one-way hash (first 8 hex of SHA-256). The body hash lets identical messages be detected without storing any text; the address hash links repeat correspondents. Message timing, type, and read-state are stored.",
        fields: [
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "Calendar day of the message (UTC).",
          },
          {
            name: "time",
            type: "timestamp",
            desc: "When the message was sent / received.",
          },
          {
            name: "time_ms",
            type: "epoch ms",
            desc: "When the message was sent / received (epoch ms).",
          },
          {
            name: "type",
            type: "string",
            values: "INBOX · SENT · DRAFT · OUTBOX · UNKNOWN",
            desc: "Message box / direction.",
          },
          {
            name: "read",
            type: "boolean",
            desc: "Whether the message had been read.",
          },
          {
            name: "address",
            type: "string",
            desc: "Hashed sender/recipient address (8-hex SHA-256; empty if unavailable).",
          },
          {
            name: "body",
            type: "string",
            desc: "Hashed message body (8-hex SHA-256); detects identical messages without storing text and is empty if blank.",
          },
          { name: "id", type: "integer", desc: "Android SMS row id." },
        ],
      },
    ],
  },
  {
    id: "audio",
    title: "Environmental & Social Context",
    blurb:
      "On-device sound and light context plus nearby Bluetooth devices as a proxy for social co-presence.",
    sources: [
      {
        source: "AMBIENT_AUDIO",
        title: "Ambient Audio",
        measures:
          "An acoustic-scene label for a short audio window (conversation / media / silence / other) plus a speech ratio.",
        cadence:
          "~10-second sample, probed every ~3 min when idle and ~1 min while a conversation is active; skipped during phone calls.",
        method: "On-device YAMNet classifies a ~10-second microphone window",
        platform: "Android",
        permission: "Microphone (RECORD_AUDIO)",
        privacy:
          "On-device YAMNet classification; no audio is stored or transmitted, only the labels below.",
        fields: [
          {
            name: "window_start",
            type: "timestamp",
            desc: "Start of the audio window.",
          },
          {
            name: "duration_s",
            type: "float",
            values: "seconds (~10)",
            desc: "Length of the window.",
          },
          {
            name: "category",
            type: "string",
            values: "conversation · media · silence · other",
            desc: "Dominant acoustic category for the window.",
          },
          {
            name: "speech_ratio",
            type: "float",
            values: "0–1",
            desc: "Fraction of frames classified as speech.",
          },
          {
            name: "top_labels",
            type: "array<object>",
            values: "[ { label: string, score: 0–1 } ]",
            desc: "Top acoustic labels and their confidence scores.",
          },
          {
            name: "in_call",
            type: "boolean",
            desc: "Whether a phone call was active (audio is skipped if true).",
          },
        ],
      },
      {
        source: "LIGHT",
        title: "Ambient Light",
        measures:
          "Ambient light level and a derived description of the surrounding light environment.",
        cadence:
          "Approximately every 15 minutes as part of the periodic collection cycle.",
        platform: "Android",
        fields: [
          {
            name: "lux",
            type: "number",
            values: "lux (≥ 0)",
            desc: "Measured ambient illuminance.",
          },
          {
            name: "light_category",
            type: "string",
            values: "dark · dim (observed)",
            desc: "Derived light-level category.",
          },
          {
            name: "indoor_outdoor",
            type: "string",
            values: "indoor (observed)",
            desc: "Derived indoor/outdoor classification.",
          },
          {
            name: "noise_db",
            type: "number | null",
            desc: "Ambient noise level in decibels when available; null in this export.",
          },
          {
            name: "noise_category",
            type: "string | null",
            desc: "Derived ambient-noise category when available; null in this export.",
          },
        ],
      },
      {
        source: "BLUETOOTH_SCAN",
        title: "Bluetooth Scan",
        measures:
          "Nearby Bluetooth-LE devices, used as a proxy for social co-presence and crowd density.",
        cadence: "5-second low-power scan every 15 minutes.",
        platform: "Android",
        permission:
          "Nearby devices (BLUETOOTH_SCAN; location on older Android)",
        privacy:
          "Hardware addresses are replaced on-device with versioned, keyed pseudonyms before storage. Advertised device names are not collected.",
        notes:
          "One row per scan. Nearby devices are nested in the devices array; an empty scan stores count=0 and an empty array.",
        fields: [
          { name: "time", type: "timestamp", desc: "When the scan ran." },
          {
            name: "count",
            type: "integer",
            values: "≥ 0",
            desc: "Number of Bluetooth-LE devices detected in the scan.",
          },
          {
            name: "pseudonym_version",
            type: "integer",
            values: "1",
            desc: "Version of the installation-scoped HMAC pseudonym scheme used for device_id.",
          },
          {
            name: "devices",
            type: "array<object>",
            values: "[ { device_id: v1_<HMAC-SHA-256>, level: dBm } ]",
            desc: "Detected devices with an installation-scoped pseudonymous identifier and signal strength.",
          },
        ],
      },
    ],
  },
  {
    id: "location",
    title: "Location",
    blurb: "GPS fixes and Wi-Fi place fingerprints.",
    sources: [
      {
        source: "AT_LOCATION",
        title: "Location",
        measures: "A GPS fix with movement and network context.",
        cadence:
          "Approximately every 15 minutes, plus an activity-triggered fix after a moving-to-stationary dwell transition.",
        platform: "Android",
        permission:
          "Location, including background (ACCESS_FINE/COARSE_LOCATION + ACCESS_BACKGROUND_LOCATION)",
        fields: [
          {
            name: "latitude",
            type: "string",
            values: "degrees",
            desc: "Latitude of the fix.",
          },
          {
            name: "longitude",
            type: "string",
            values: "degrees",
            desc: "Longitude of the fix.",
          },
          {
            name: "accuracy",
            type: "number",
            values: "meters",
            desc: "OS-reported horizontal accuracy radius (smaller is better). Coarse fixes up to ~150 m are accepted, so values near 100 are genuine coarse fixes, not errors.",
          },
          {
            name: "time_of_day",
            type: "string",
            values: "morning · afternoon · evening · night",
            desc: "Bucketed time of the fix.",
          },
          {
            name: "network_type",
            type: "string",
            values: "wifi · cellular · unknown · none",
            desc: "Active network at the fix.",
          },
          {
            name: "source",
            type: "string",
            values: "periodic · activity_transition",
            desc: "What triggered the fix.",
          },
        ],
      },
      {
        source: "WIFI_EVENT",
        title: "Wi-Fi Scan",
        measures: "A scan of nearby Wi-Fi access points (a place fingerprint).",
        cadence: "5-second scan every 15 minutes.",
        platform: "Android",
        permission: "Location / Nearby Wi-Fi devices (for scans)",
        privacy:
          "Access-point hardware ids are replaced on-device with versioned, keyed pseudonyms before storage. Wi-Fi network names are not stored. APs weaker than -85 dBm are dropped.",
        fields: [
          { name: "time", type: "timestamp", desc: "When the scan ran." },
          {
            name: "count",
            type: "integer",
            desc: "Number of access points after filtering.",
          },
          {
            name: "pseudonym_version",
            type: "integer",
            values: "1",
            desc: "Version of the installation-scoped HMAC pseudonym scheme used for access_point_id.",
          },
          {
            name: "networks",
            type: "array<object>",
            values: "[ { access_point_id: v1_<HMAC-SHA-256>, freq: MHz, level: dBm } ]",
            desc: "Access points seen (installation-scoped pseudonym, channel frequency, signal strength).",
          },
        ],
      },
    ],
  },
  {
    id: "sleep",
    title: "Sleep",
    blurb:
      "Per-sample in-bed classification from Google's Sleep API. (Consolidated sleep timing/duration comes from Health Connect, not these records.)",
    sources: [
      {
        source: "SLEEP",
        title: "Sleep Classification",
        measures:
          "Per-sample in-bed classification from Google's Sleep API (sleep confidence with motion and light).",
        cadence:
          "Event-driven, with one record per Sleep API classify sample (~every 10 minutes).",
        method:
          "Google's Sleep API (Activity Recognition) delivers SleepClassifyEvent samples to a broadcast receiver",
        platform: "Android",
        permission: "Physical activity (ACTIVITY_RECOGNITION)",
        fields: [
          {
            name: "source",
            type: "string",
            values: "ANDROID_SLEEP_API",
            desc: "Origin of the sample.",
          },
          {
            name: "confidence",
            type: "integer",
            values: "0–100",
            desc: "Sleep confidence; higher means more likely asleep.",
          },
          {
            name: "motion",
            type: "integer",
            values: "0–6 (Google scale; higher = more motion)",
            desc: "Device motion level for the sample.",
          },
          {
            name: "light",
            type: "integer",
            values: "0–6 (Google scale; higher = brighter)",
            desc: "Ambient light level for the sample.",
          },
        ],
      },
    ],
  },
  {
    id: "device",
    title: "Device State",
    blurb: "Battery and charging.",
    sources: [
      {
        source: "BATTERY_LEVEL",
        title: "Battery Level",
        measures: "Periodic battery percentage and charging state.",
        cadence:
          "Approximately every 15 minutes as part of the periodic collection cycle.",
        platform: "Android",
        fields: [
          {
            name: "battery_level",
            type: "integer",
            values: "0–100 (percent)",
            desc: "Battery percentage at the time of the sample.",
          },
          {
            name: "state",
            type: "string",
            values: "CHARGING · FULL · UNPLUGGED · UNKNOWN",
            desc: "Charging state at the time of the sample.",
          },
        ],
      },
      {
        source: "CHARGING_EVENT",
        title: "Charging Event",
        measures: "Battery charging-state changes.",
        cadence: "Event-driven on power connect, disconnect, or reaching full.",
        platform: "Android",
        fields: [
          {
            name: "state",
            type: "string",
            values: "CHARGING · FULL · UNPLUGGED · UNKNOWN",
            desc: "New charging state.",
          },
          {
            name: "battery_level",
            type: "integer",
            values: "0–100 (percent)",
            desc: "Battery percentage at the change.",
          },
        ],
      },
    ],
  },
  {
    id: "health",
    title: "Health Connect",
    blurb:
      "Daily health metrics read from Android Health Connect. This source is Android-only and optional (requires the participant to use Health Connect and grant access).",
    sources: [
      {
        source: "HEALTH_CONNECT",
        title: "Health Connect",
        measures:
          "Daily health metrics aggregated from Android Health Connect.",
        cadence: "Once per day (for the previous day).",
        method:
          "Reads and aggregates the previous day's metrics from Android Health Connect.",
        platform: "Android only (optional)",
        permission: "Health Connect runtime permissions (per data type)",
        notes:
          "Only fields for which the participant granted access and Health Connect supplied data are included. Aggregate metrics may be zero when permission is granted but no records exist.",
        fields: [
          {
            name: "date",
            type: "string",
            values: "YYYY-MM-DD",
            desc: "The day the metrics cover.",
          },
          {
            name: "steps",
            type: "integer",
            values: "≥ 0",
            desc: "Aggregated step total for the day.",
          },
          {
            name: "heart_rate",
            type: "object",
            values: "{ avg_bpm, min_bpm, max_bpm, sample_count }",
            desc: "Daily aggregate of heart-rate samples in beats per minute.",
          },
          {
            name: "resting_heart_rate",
            type: "integer",
            values: "beats per minute",
            desc: "Average of the day's resting-heart-rate records.",
          },
          {
            name: "blood_pressure",
            type: "array<object>",
            values: "[ { systolic, diastolic, time } ]",
            desc: "Blood-pressure readings in mmHg with their timestamps.",
          },
          {
            name: "oxygen_saturation",
            type: "object",
            values: "{ avg_percent, min_percent, sample_count }",
            desc: "Daily aggregate of blood-oxygen saturation readings.",
          },
          {
            name: "body_temperature",
            type: "array<object>",
            values: "[ { celsius, measurement_location, time } ]",
            desc: "Body-temperature readings with measurement location and timestamp.",
          },
          {
            name: "respiratory_rate",
            type: "object",
            values: "{ avg_rate, sample_count }",
            desc: "Average respiratory rate and number of samples.",
          },
          {
            name: "weight",
            type: "object",
            values: "{ kg, time }",
            desc: "Latest weight record for the day.",
          },
          {
            name: "height",
            type: "object",
            values: "{ meters, time }",
            desc: "Latest height record for the day.",
          },
          {
            name: "sleep_sessions",
            type: "array<object>",
            values: "[ { start_time, end_time, stages: [{ stage, start_time, end_time }] } ]",
            desc: "Sleep sessions overlapping the day, including any recorded sleep stages.",
          },
          {
            name: "distance",
            type: "number",
            values: "meters",
            desc: "Aggregated distance traveled during the day.",
          },
          {
            name: "active_calories",
            type: "number",
            values: "kilocalories",
            desc: "Aggregated active calories burned during the day.",
          },
          {
            name: "total_calories",
            type: "number",
            values: "kilocalories",
            desc: "Aggregated total calories burned during the day.",
          },
          {
            name: "exercise_sessions",
            type: "array<object>",
            values: "[ { type, start_time, end_time, title? } ]",
            desc: "Exercise sessions recorded for the day; title is included when supplied by Health Connect.",
          },
          {
            name: "body_fat",
            type: "object",
            values: "{ percentage, time }",
            desc: "Latest body-fat percentage record for the day.",
          },
        ],
      },
    ],
  },
  {
    id: "calendar",
    title: "Calendar",
    blurb:
      "Scheduled calendar events, including timing, structure, pseudonymized metadata, and a derived event category.",
    sources: [
      {
        source: "CALENDAR_EVENT",
        title: "Calendar Event",
        measures:
          "One record per calendar event occurrence in the current local day.",
        cadence:
          "Checked hourly for the current local day; each occurrence is stored once. Recurring events produce one record per occurrence.",
        method:
          "Reads the device Calendar Provider (CalendarContract.Instances), which expands recurring events into individual occurrences and exposes each event's metadata.",
        platform: "Android",
        permission: "Calendar (READ_CALENDAR)",
        privacy:
          "The event title, location, organizer, and description are never stored in the clear; the stored payload contains short one-way hashes (first 8 hex of SHA-256). Title and description are sent transiently to Firebase AI Logic (Gemini) to derive a fixed category, but are not returned in the result, written to the local event payload, or logged. Attendee names and emails are represented only by short hashes; counts and hashed identities are retained.",
        notes:
          "All-day events are bucketed by their nominal (UTC) date; timed events by their local-day start.",
        fields: [
          {
            name: "event_id",
            type: "integer",
            desc: "Calendar Provider event id; links occurrences of the same recurring event.",
          },
          {
            name: "calendar_id_hash",
            type: "string",
            desc: "Hashed id of the calendar the event belongs to (8-hex SHA-256); distinguishes calendars without identifying the account.",
          },
          {
            name: "begin",
            type: "epoch ms",
            desc: "Start of this occurrence (also the record's collected_at).",
          },
          { name: "end", type: "epoch ms", desc: "End of this occurrence." },
          {
            name: "all_day",
            type: "integer",
            values: "0 · 1",
            desc: "Whether the event is all-day.",
          },
          {
            name: "duration",
            type: "string | null",
            values: "RFC5545 (e.g. PT1H)",
            desc: "Event duration; present instead of a fixed end on some recurring events.",
          },
          {
            name: "event_timezone",
            type: "string | null",
            desc: "Time zone the event is scheduled in (e.g. America/Phoenix).",
          },
          {
            name: "rrule",
            type: "string | null",
            values: "RFC5545 (e.g. FREQ=WEEKLY)",
            desc: "Recurrence rule, if the event repeats.",
          },
          {
            name: "availability",
            type: "integer | null",
            values: "0 busy · 1 free · 2 tentative",
            desc: "How the event marks the participant's time.",
          },
          {
            name: "status",
            type: "integer | null",
            values: "0 tentative · 1 confirmed · 2 canceled",
            desc: "Event status.",
          },
          {
            name: "access_level",
            type: "integer | null",
            values: "0 default · 1 confidential · 2 private · 3 public",
            desc: "Event classification / sensitivity.",
          },
          {
            name: "self_attendee_status",
            type: "integer | null",
            values:
              "0 none · 1 accepted · 2 declined · 3 invited · 4 tentative",
            desc: "The participant's own RSVP to the event.",
          },
          {
            name: "is_organizer",
            type: "integer | null",
            values: "0 · 1",
            desc: "Whether the participant organizes the event.",
          },
          {
            name: "has_alarm",
            type: "integer",
            values: "0 · 1",
            desc: "Whether the event has a reminder set.",
          },
          {
            name: "has_attendee_data",
            type: "integer",
            values: "0 · 1",
            desc: "Whether the event has any guests.",
          },
          {
            name: "attendee_count",
            type: "integer",
            values: "≥ 0",
            desc: "Number of guests on this event (0 if none / not shared).",
          },
          {
            name: "attendees",
            type: "array<object>",
            values: "[ { attendee_id_hash, relationship, type, status } ]",
            desc: "One entry per guest. attendee_id_hash = 8-hex SHA-256 of the guest's email (else identity, else name); links a recurring co-attendee across events without revealing who. relationship: 0 none · 1 attendee · 2 organizer · 3 performer · 4 speaker. type: 0 none · 1 required · 2 optional · 3 resource. status: 0 none · 1 accepted · 2 declined · 3 invited · 4 tentative.",
          },
          {
            name: "category",
            type: "string",
            values:
              "Health / Medical · Work / Professional · Social · Family · Errand / Chore · Physical Activity · Religious / Spiritual · Community / Volunteer · Travel · Personal · Other",
            desc: "AI-derived category constrained to the fixed taxonomy shown here.",
          },
          {
            name: "taxonomy_version",
            type: "integer",
            values: "1",
            desc: "Version of the category taxonomy used to classify this event.",
          },
          {
            name: "classification_status",
            type: "string | absent",
            values: "fallback · absent on successful classification",
            desc: "Present as fallback when classification still fails after retries; category is Other in that case.",
          },
          {
            name: "title_hash",
            type: "string",
            desc: "Hashed event title (8-hex SHA-256; empty if untitled)",
          },
          {
            name: "event_location_hash",
            type: "string",
            desc: "Hashed event location (8-hex SHA-256; empty if none)",
          },
          {
            name: "organizer_hash",
            type: "string",
            desc: "Hashed organizer email (8-hex SHA-256; empty if none)",
          },
          {
            name: "description_hash",
            type: "string",
            desc: "Hashed event description / notes (8-hex SHA-256; empty if none); links repeated descriptions without storing the text.",
          },
        ],
      },
    ],
  },
  {
    id: "system",
    title: "System",
    blurb: "A background-service health heartbeat (diagnostic).",
    sources: [
      {
        source: "ALIVE",
        title: "Service Heartbeat",
        measures:
          "Background-service health heartbeat. Confirms the collector was alive for a 15-minute window. Not a behavioral/research signal.",
        cadence: "Every 15 minutes.",
        platform: "Android",
        fields: [
          {
            name: "platform",
            type: "string",
            values: "android",
            desc: "Reporting platform.",
          },
          {
            name: "service_running",
            type: "boolean",
            desc: "Whether the foreground service was alive.",
          },
          {
            name: "window_start_ms",
            type: "epoch ms",
            desc: "Start of the 15-minute reporting window.",
          },
        ],
      },
    ],
  },
];
